import { uploaderAxios, giftUploaderAxios } from './uploaderApi';
import uploaderEndpoints from './uploaderEndpoints.json';

/**
 * Upload Service API Functions
 *
 * These functions interact with the secure upload service (uploader.projector.app)
 * that provides presigned URLs for S3 uploads, eliminating the need for
 * AWS credentials in the client.
 */

/**
 * Create upload session for a file
 *
 * This initiates a multipart upload and returns presigned URLs for uploading parts.
 *
 * @param {Object} params - Upload parameters
 * @param {string} params.file_name - Name of the file
 * @param {number} params.file_size - Size of the file in bytes
 * @param {string} params.content_type - MIME type (e.g., 'video/mp4')
 * @param {string} params.s3_key - S3 key path (e.g., 'cloud/production/user123/1234567890_video.mp4')
 *
 * @returns {Promise<Object>} Session data:
 *   - id: Session ID for tracking
 *   - presigned_urls: Array of { part_number, presigned_url, expires_at }
 *   - part_size: Size of each part in bytes
 *   - total_parts: Number of parts to upload
 *   - websocket_url: WebSocket URL for progress tracking
 */
export const createUploadSession = async (params) => {
  try {
    console.log('[UploadAPI] Creating upload session:', {
      fileName: params.file_name,
      fileSize: params.file_size,
      s3Key: params.s3_key,
    });

    const response = await uploaderAxios.post(uploaderEndpoints.createSession, params);

    console.log('[UploadAPI] Session created:', {
      sessionId: response.data.id,
      totalParts: response.data.total_parts,
    });

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to create upload session:', error);
    throw new Error(`Failed to create upload session: ${error.message}`);
  }
};

/**
 * Complete upload session
 *
 * Finalizes the multipart upload by combining all uploaded parts.
 *
 * @param {string} sessionId - Upload session ID
 * @param {Array<Object>} parts - Array of uploaded parts
 * @param {number} parts[].PartNumber - Part number (1-indexed)
 * @param {string} parts[].ETag - ETag from S3 upload response
 *
 * @returns {Promise<Object>} Completion data:
 *   - success: Boolean indicating success
 *   - url: Final S3 URL of the uploaded file
 */
export const completeUploadSession = async (sessionId, parts) => {
  try {
    console.log('[UploadAPI] Completing upload session:', {
      sessionId,
      partsCount: parts.length,
    });

    const url = uploaderEndpoints.completeSession.replace(':id', sessionId);
    const response = await uploaderAxios.post(url, { parts });

    console.log('[UploadAPI] Session completed successfully');

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to complete upload session:', error);
    throw new Error(`Failed to complete upload session: ${error.message}`);
  }
};

/**
 * Refresh presigned URLs for a session
 *
 * Used when presigned URLs expire during a long upload or after resume.
 *
 * @param {string} sessionId - Upload session ID
 * @param {Array<number>} partNumbers - Array of part numbers that need new URLs
 *
 * @returns {Promise<Object>} Refreshed URLs:
 *   - presigned_urls: Array of { part_number, presigned_url, expires_at }
 */
export const refreshPresignedUrls = async (sessionId, partNumbers) => {
  try {
    console.log('[UploadAPI] Refreshing presigned URLs:', {
      sessionId,
      partNumbers,
    });

    const url = uploaderEndpoints.refreshUrls.replace(':id', sessionId);
    const response = await uploaderAxios.post(url, { part_numbers: partNumbers });

    console.log('[UploadAPI] URLs refreshed successfully');

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to refresh presigned URLs:', error);
    throw new Error(`Failed to refresh presigned URLs: ${error.message}`);
  }
};

/**
 * Get resume information for a session
 *
 * Returns which parts have been uploaded and which remain.
 *
 * @param {string} sessionId - Upload session ID
 *
 * @returns {Promise<Object>} Resume data:
 *   - uploaded_parts: Array of part numbers already uploaded
 *   - remaining_parts: Array of part numbers still needed
 *   - bytes_uploaded: Total bytes uploaded so far
 *   - total_bytes: Total file size
 *   - presigned_urls: Fresh presigned URLs for remaining parts
 */
export const getResumeInfo = async (sessionId) => {
  try {
    console.log('[UploadAPI] Getting resume info for session:', sessionId);

    const url = uploaderEndpoints.resumeSession.replace(':id', sessionId);
    const response = await uploaderAxios.get(url);

    console.log('[UploadAPI] Resume info retrieved:', {
      uploadedParts: response.data.uploaded_parts?.length || 0,
      remainingParts: response.data.remaining_parts?.length || 0,
    });

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to get resume info:', error);
    throw new Error(`Failed to get resume info: ${error.message}`);
  }
};

/**
 * Register document metadata after upload
 *
 * Called after the file is fully uploaded to S3 to register it in the system.
 *
 * @param {Object} data - Document metadata
 * @param {string} data.name - File name
 * @param {string} data.url - S3 key path
 * @param {string} data.thumbnail - Thumbnail S3 key
 * @param {Array<string>} data.thumbnails - Array of thumbnail S3 keys
 * @param {string} data.uploaded_at - ISO timestamp of upload
 *
 * @returns {Promise<Object>} Document data:
 *   - id: Document ID
 *   - size: File size in bytes
 */
export const registerDocument = async (data) => {
  try {
    console.log('[UploadAPI] Registering document:', {
      name: data.name,
      url: data.url,
    });

    const response = await uploaderAxios.post(uploaderEndpoints.registerDocument, data);

    console.log('[UploadAPI] Document registered:', {
      documentId: response.data.id,
      size: response.data.size,
    });

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to register document:', error);
    throw new Error(`Failed to register document: ${error.message}`);
  }
};

/**
 * Link draft to document
 *
 * Creates a link between the draft (from main backend) and the document (from upload service).
 *
 * @param {Object} data - Link data
 * @param {number} data.document_id - Document ID from registerDocument
 * @param {string} data.draft_id - Draft ID from backend addVideo API
 *
 * @returns {Promise<Object>} Link confirmation:
 *   - success: Boolean indicating success
 */
export const updateDraftLink = async (data) => {
  try {
    console.log('[UploadAPI] Linking draft to document:', {
      documentId: data.document_id,
      draftId: data.draft_id,
    });

    const response = await uploaderAxios.put(uploaderEndpoints.updateDraft, data);

    console.log('[UploadAPI] Draft linked successfully');

    return response.data;
  } catch (error) {
    console.error('[UploadAPI] Failed to link draft:', error);
    throw new Error(`Failed to link draft: ${error.message}`);
  }
};

/**
 * Upload a single part using presigned URL
 *
 * This is a helper function that performs the actual PUT request to S3.
 *
 * @param {string} presignedUrl - Presigned URL from createUploadSession
 * @param {Blob} blob - File part data
 * @param {number} partNumber - Part number (1-indexed)
 * @param {Function} onProgress - Optional progress callback (loaded, total) => void
 *
 * @returns {Promise<Object>} Part upload result:
 *   - PartNumber: Part number
 *   - ETag: ETag from S3 (required for completion)
 */
export const uploadPartWithPresignedUrl = async (
  presignedUrl,
  blob,
  partNumber,
  onProgress = null,
  xhrRef = null,
  stallTimeoutMs = 25000
) => {
  try {
    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Expose XHR for external abort (e.g., cancel upload)
      if (xhrRef) {
        Object.assign(xhrRef, { current: xhr });
      }

      // ── Stall watchdog ─────────────────────────────────────────────────
      // A plain XHR has NO timeout: if the device sleeps/locks or the network
      // drops mid-part, the request can hang forever without firing load or
      // error — the awaiting upload loop then freezes with no retry (the
      // classic "stuck where it last uploaded"). We arm a timer that aborts
      // the request if no bytes move for `stallTimeoutMs`, and reset it on
      // every progress tick so a slow-but-alive connection is never killed.
      let settled = false;
      let stalled = false;
      let stallTimer = null;
      const clearStall = () => {
        if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
      };
      const armStall = () => {
        if (!stallTimeoutMs) return;
        clearStall();
        stallTimer = setTimeout(() => {
          // Flag so the abort handler reports a stall, not a user abort.
          stalled = true;
          try { xhr.abort(); } catch (e) { /* noop */ }
        }, stallTimeoutMs);
      };

      xhr.upload.addEventListener('loadstart', armStall);
      xhr.upload.addEventListener('progress', (event) => {
        armStall(); // bytes are flowing — reset the watchdog
        if (event.lengthComputable && onProgress) {
          onProgress(event.loaded, event.total);
        }
      });
      // Keep the watchdog alive while we wait for the server's response after
      // the body is fully sent (a hung server should also time out).
      xhr.upload.addEventListener('load', armStall);

      xhr.addEventListener('load', () => {
        settled = true;
        clearStall();
        if (xhr.status >= 200 && xhr.status < 300) {
          // Extract ETag from response headers
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error('No ETag in response'));
            return;
          }

          resolve({
            PartNumber: partNumber,
            ETag: etag.replace(/"/g, ''), // Remove quotes from ETag
          });
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        settled = true;
        clearStall();
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        if (settled) return;
        settled = true;
        clearStall();
        reject(new Error(stalled ? 'Upload stalled' : 'Upload aborted'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.send(blob);
      armStall(); // guard the window before the first progress event fires
    });
  } catch (error) {
    console.error(`[UploadAPI] Failed to upload part ${partNumber}:`, error);
    throw error;
  }
};

// ============================================================
// Gift Upload Functions (UUID-based auth, no Bearer token)
// ============================================================

/**
 * Create gift upload session
 *
 * @param {string} giftUuid - Gift user UUID
 * @param {Object} params - { file_name, file_size, content_type }
 * @returns {Promise<Object>} Session with presigned_urls[], websocket_url
 */
export const createGiftUploadSession = async (giftUuid, params) => {
  try {
    console.log('[GiftUploadAPI] Creating gift upload session:', {
      giftUuid,
      fileName: params.file_name,
      fileSize: params.file_size,
    });

    const response = await giftUploaderAxios.post(uploaderEndpoints.giftCreateSession, {
      gift_uuid: giftUuid,
      file_name: params.file_name,
      file_size: params.file_size,
      content_type: params.content_type,
    });

    console.log('[GiftUploadAPI] Session created:', {
      sessionId: response.data.id,
      totalParts: response.data.total_parts,
    });

    return response.data;
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to create gift upload session:', error);
    throw new Error(`Failed to create gift upload session: ${error.message}`);
  }
};

/**
 * Complete gift upload session
 *
 * @param {string} sessionId - Upload session ID
 * @param {string} giftUuid - Gift user UUID
 * @param {Array<Object>} parts - Array of { PartNumber, ETag }
 * @returns {Promise<Object>} Completed session with file_url
 */
export const completeGiftUploadSession = async (sessionId, giftUuid, parts) => {
  try {
    console.log('[GiftUploadAPI] Completing gift upload session:', {
      sessionId,
      giftUuid,
      partsCount: parts.length,
    });

    const url = `${uploaderEndpoints.giftCompleteSession.replace(':id', sessionId)}?gift_uuid=${giftUuid}`;
    const response = await giftUploaderAxios.post(url, { parts });

    console.log('[GiftUploadAPI] Session completed successfully');

    return response.data;
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to complete gift upload session:', error);
    throw new Error(`Failed to complete gift upload session: ${error.message}`);
  }
};

/**
 * Refresh presigned URLs for a gift upload session
 *
 * @param {string} sessionId - Upload session ID
 * @param {string} giftUuid - Gift user UUID
 * @param {Array<number>} partNumbers - Part numbers that need new URLs
 * @returns {Promise<Object>} { presigned_urls: [...] }
 */
export const refreshGiftPresignedUrls = async (sessionId, giftUuid, partNumbers) => {
  try {
    console.log('[GiftUploadAPI] Refreshing gift presigned URLs:', {
      sessionId,
      giftUuid,
      partNumbers,
    });

    const url = `${uploaderEndpoints.giftRefreshUrls.replace(':id', sessionId)}?gift_uuid=${giftUuid}`;
    const response = await giftUploaderAxios.post(url, { part_numbers: partNumbers });

    console.log('[GiftUploadAPI] URLs refreshed successfully');

    return response.data;
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to refresh gift presigned URLs:', error);
    throw new Error(`Failed to refresh gift presigned URLs: ${error.message}`);
  }
};

/**
 * Get resume info for a gift upload session
 *
 * @param {string} sessionId - Upload session ID
 * @param {string} giftUuid - Gift user UUID
 * @returns {Promise<Object>} { uploaded_parts, remaining_parts, presigned_urls, bytes_uploaded }
 */
export const getGiftResumeInfo = async (sessionId, giftUuid) => {
  try {
    console.log('[GiftUploadAPI] Getting gift resume info:', { sessionId, giftUuid });

    const url = `${uploaderEndpoints.giftResumeSession.replace(':id', sessionId)}?gift_uuid=${giftUuid}`;
    const response = await giftUploaderAxios.get(url);

    console.log('[GiftUploadAPI] Resume info retrieved:', {
      uploadedParts: response.data.uploaded_parts?.length || 0,
      remainingParts: response.data.remaining_parts?.length || 0,
    });

    return response.data;
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to get gift resume info:', error);
    throw new Error(`Failed to get gift resume info: ${error.message}`);
  }
};

/**
 * Get gift upload session details
 *
 * @param {string} sessionId - Upload session ID
 * @param {string} giftUuid - Gift user UUID
 * @returns {Promise<Object>} Session details
 */
export const getGiftSession = async (sessionId, giftUuid) => {
  try {
    console.log('[GiftUploadAPI] Getting gift session:', { sessionId, giftUuid });

    const url = `${uploaderEndpoints.giftGetSession.replace(':id', sessionId)}?gift_uuid=${giftUuid}`;
    const response = await giftUploaderAxios.get(url);

    return response.data;
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to get gift session:', error);
    throw new Error(`Failed to get gift session: ${error.message}`);
  }
};

/**
 * Cancel a gift upload session
 *
 * @param {string} sessionId - Upload session ID
 * @param {string} giftUuid - Gift user UUID
 * @returns {Promise<void>}
 */
export const cancelGiftUploadSession = async (sessionId, giftUuid) => {
  try {
    console.log('[GiftUploadAPI] Cancelling gift upload session:', { sessionId, giftUuid });

    const url = `${uploaderEndpoints.giftCancelSession.replace(':id', sessionId)}?gift_uuid=${giftUuid}`;
    await giftUploaderAxios.delete(url);

    console.log('[GiftUploadAPI] Session cancelled successfully');
  } catch (error) {
    console.error('[GiftUploadAPI] Failed to cancel gift upload session:', error);
    throw new Error(`Failed to cancel gift upload session: ${error.message}`);
  }
};

/**
 * Fetch the emails allowed to use the new UI.
 *
 * The list used to be a hardcoded array in hooks/useNewUI; it now lives on the
 * upload service so it can change without a release. Paginated (DRF style), so
 * follow `next` until it runs out.
 *
 * @returns {Promise<string[]>} lowercase emails; throws so the caller can keep
 *   whatever it had cached rather than treating a failure as "nobody allowed".
 */
export const getWhitelistedEmails = async () => {
  const emails = [];
  let url = uploaderEndpoints.whitelistedEmails;
  while (url) {
    // Sequential by necessity: each page's `next` is only known once the
    // previous page has come back.
    // eslint-disable-next-line no-await-in-loop
    const { data } = await uploaderAxios.get(url);
    (data?.results || []).forEach((row) => {
      const email = (row?.email || '').trim().toLowerCase();
      if (email) emails.push(email);
    });
    url = data?.next || null;
  }
  return emails;
};
